import React, { useMemo } from "react";

/**
 * ONE canonical presentation of the class's real grade → subjects
 * mapping for the attendance page. One row per grade in the form
 *   Grade 3 — English, Science, Math
 *   Grade 4 — English, ABA Therapy
 * Rendered in `variant="dark"` inside the dark blue sticky header, and
 * `variant="light"` inside the white Class card below — same source,
 * same layout, colours switched.
 *
 * Never flattens grades × subjects into a single sentence. Never
 * repeats a subject within one grade. Sorts grades by explicit
 * sort_order else the app's canonical order (Pre-K, JK, SK, 1..12,
 * Adult). Subjects within a grade sort by sort_order then name.
 */

export interface GradeSubjectsBucket {
  grade_id: number;
  grade: { id: number; name: string; sort_order?: number | null };
  subjects: Array<{ id: number; name: string; sort_order?: number | null }>;
}

interface Props {
  groups?: GradeSubjectsBucket[] | null;
  variant?: "dark" | "light";
  compact?: boolean;
}

const CANONICAL_GRADE_ORDER = [
  "pre-k", "prek", "pre k",
  "jk", "junior kindergarten",
  "sk", "senior kindergarten",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
  "adult",
] as const;
const canonicalGradeRank = (name: string): number => {
  const n = String(name || "").trim().toLowerCase();
  const direct = CANONICAL_GRADE_ORDER.indexOf(n as any);
  if (direct >= 0) return direct;
  const stripped = n.replace(/^grade\s*/, "");
  const asIdx = CANONICAL_GRADE_ORDER.indexOf(stripped as any);
  return asIdx >= 0 ? asIdx : 999;
};

const GradeSubjectsSummary: React.FC<Props> = ({ groups, variant = "dark", compact = false }) => {
  const isDark = variant === "dark";
  const labelColor = isDark ? "#ffffff" : "#1b3a6b";
  const gradeColor = isDark ? "#ffffff" : "#1b3a6b";
  const subjectColor = isDark ? "rgba(255,255,255,0.88)" : "#334155";

  const clean = useMemo(() => {
    const source = Array.isArray(groups) ? groups : [];
    // Per-grade subject dedup by id (fallback to normalised name).
    const shaped = source
      .map((g) => {
        const seenIds = new Set<number>();
        const seenNames = new Set<string>();
        const subjects = (g.subjects || []).filter((s) => {
          const name = String(s.name || "").trim();
          if (!name) return false;
          if (s.id != null) {
            if (seenIds.has(s.id)) return false;
            seenIds.add(s.id);
            return true;
          }
          const key = name.toLowerCase();
          if (seenNames.has(key)) return false;
          seenNames.add(key);
          return true;
        });
        subjects.sort((a, b) => {
          const oa = a.sort_order ?? null;
          const ob = b.sort_order ?? null;
          if (oa != null && ob != null && oa !== ob) return oa - ob;
          return String(a.name).localeCompare(String(b.name));
        });
        return { ...g, subjects };
      })
      .filter((g) => g.grade?.name && g.subjects.length > 0);

    shaped.sort((a, b) => {
      const oa = a.grade?.sort_order ?? null;
      const ob = b.grade?.sort_order ?? null;
      if (oa != null && ob != null && oa !== ob) return oa - ob;
      return canonicalGradeRank(a.grade.name) - canonicalGradeRank(b.grade.name);
    });
    return shaped;
  }, [groups]);

  if (clean.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 2 : 4, minWidth: 0 }}>
      {clean.map((g) => {
        const gname = g.grade.name;
        const label = /^(pre-?k|jk|sk|adult|\d+)$/i.test(gname.trim()) ? `Grade ${gname}` : gname;
        return (
          <div
            key={g.grade_id}
            style={{
              color: labelColor,
              fontSize: compact ? 13 : 14,
              lineHeight: 1.4,
              wordBreak: "normal",
              overflowWrap: "anywhere",
            }}
          >
            <span style={{ fontWeight: 700, color: gradeColor }}>{label}</span>
            <span style={{ margin: "0 6px", color: subjectColor, opacity: 0.7 }}>—</span>
            <span style={{ color: subjectColor, fontWeight: 500 }}>
              {g.subjects.map((s) => s.name).join(", ")}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default GradeSubjectsSummary;
